import { Paginator } from "@/components/paginator";
import { PayByLinkForm } from "@/routes/(app)/_main-layout/_pay-by-link/-components/form";
import { StatusTag } from "@/components/status-tag";
import { EU } from "@/components/svg/eu";
import { Gbp } from "@/components/svg/gbp";
import { CardWithHeader } from "@/components/card-with-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { hasRole } from "@/lib/auth";
import { numberToDecimalWithCurrency } from "@/lib/utils";
import { VolumeApi } from "@/lib/volume-api";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { MoreHorizontal, Eye } from "lucide-react";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod/v4";

export const Route = createFileRoute(
  "/(app)/_main-layout/_pay-by-link/pay-by-link",
)({
  validateSearch: zodValidator(
    z.object({ page: z.number().optional().catch(1).default(1) }),
  ),
  component: () => <PayByLinkComponent />,
  beforeLoad: ({ context }) => {
    if (!hasRole("PAY_BY_LINK", context.authorities)) {
      throw redirect({ to: "/", search: true });
    }
    return context;
  },
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: ({ context: { selectedApplicationId, accessToken }, deps }) => {
    const api = new VolumeApi(accessToken, selectedApplicationId);
    return api.getRecentPaymentIntents({
      applicationId: selectedApplicationId,
      page: deps.page - 1,
    });
  },
});

const PayByLinkComponent = () => {
  const recentPaymentLinks = Route.useLoaderData();

  return (
    <>
      <div className="mt-4">
        <CardWithHeader
          variant="exchange"
          label="Pay by Link"
          title="Generate a new payment link"
        >
          <PayByLinkForm />
        </CardWithHeader>
      </div>
      <div className="my-8 w-full">
        <CardWithHeader
          variant="exchange"
          label="Pay by Link"
          title="Recent payment links"
          noChildPadding
        >
          <div className="overflow-x-auto">
            <Table className="text-left text-sm text-slate-600">
              <TableHeader className="border-b border-slate-200 bg-slate-50/70 text-xs uppercase text-slate-700">
                <TableRow>
                  <TableHead
                    scope="col"
                    className="w-[24%] px-6 py-3 whitespace-nowrap"
                  >
                    Merchant Payment ID
                  </TableHead>
                  <TableHead
                    scope="col"
                    className="w-[12%] px-6 py-3 whitespace-nowrap"
                  >
                    Currency
                  </TableHead>
                  <TableHead
                    scope="col"
                    className="w-[16%] px-6 py-3 whitespace-nowrap"
                  >
                    Amount
                  </TableHead>
                  <TableHead
                    scope="col"
                    className="w-[22%] px-6 py-3 whitespace-nowrap"
                  >
                    Expires at
                  </TableHead>
                  <TableHead
                    scope="col"
                    className="w-[16%] px-6 py-3 whitespace-nowrap"
                  >
                    Status
                  </TableHead>
                  <TableHead
                    scope="col"
                    className="w-[10%] px-6 py-3 text-right whitespace-nowrap"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentPaymentLinks.page.numberOfElements === 0 ? (
                  <TableRow className="w-full text-center text-base font-normal">
                    <TableCell className="pt-5" colSpan={6}>
                      No payments found
                    </TableCell>
                  </TableRow>
                ) : null}
                {recentPaymentLinks.page.content.map((paymentIntent) => (
                  <TableRow
                    key={paymentIntent.paymentIntentId}
                    className="border-b border-slate-200 bg-white hover:bg-volume-light-purple/10"
                  >
                    <TableCell className="px-6 py-4 font-medium text-slate-700">
                      <Link
                        className="text-volume-text-purple underline"
                        to="/pay-by-link/$paymentIntentId"
                        params={{
                          paymentIntentId: paymentIntent.paymentIntentId,
                        }}
                        search
                      >
                        {paymentIntent.merchantPaymentId}
                      </Link>
                    </TableCell>
                    <TableCell className="px-6 py-4 whitespace-nowrap text-slate-700">
                      <div className="flex items-center gap-2">
                        {paymentIntent.paymentRequest.currency === "GBP" ? (
                          <Gbp />
                        ) : (
                          <EU />
                        )}
                        {paymentIntent.paymentRequest.currency}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {numberToDecimalWithCurrency(
                        paymentIntent.paymentRequest.amount,
                        paymentIntent.paymentRequest.currency,
                      )}
                    </TableCell>
                    <TableCell className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {new Date(paymentIntent.expiryTimeUtc).toLocaleString()}
                    </TableCell>
                    <TableCell className="w-20 px-6 py-4">
                      {paymentIntent.activePaymentStatus ? (
                        <StatusTag
                          paymentStatus={
                            paymentIntent.activePaymentStatus as never
                          }
                        />
                      ) : (
                        <div />
                      )}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      {(paymentIntent.activePaymentStatus === "COMPLETED" ||
                        paymentIntent.activePaymentStatus === "SETTLED") &&
                      paymentIntent.activePaymentId ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger className="focus:outline-hidden">
                            <MoreHorizontal className="rounded-xl hover:bg-volume-text-purple/20" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <Link
                              to="/payments/$paymentId"
                              params={{
                                paymentId: paymentIntent.activePaymentId,
                              }}
                              search
                              className="flex"
                            >
                              <DropdownMenuItem className="hover:cursor-pointer">
                                <Eye className="ml-1 mr-4" /> Details
                              </DropdownMenuItem>
                            </Link>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {recentPaymentLinks.page.totalPages > 1 ? (
            <div className="py-2 pb-5">
              <Paginator
                from={Route.id}
                totalPages={recentPaymentLinks.page.totalPages}
              />
            </div>
          ) : null}
        </CardWithHeader>
      </div>
    </>
  );
};
